import { User } from "@/types/user";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import posthog from "posthog-js";
import { queryClient } from "@/lib/queryClient";

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => void;
}

export function useAuth(): AuthContextType {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  const loadAuthFromStorage = () => {
    const storedToken = localStorage.getItem("adminToken");
    const storedUser = localStorage.getItem("adminUser");

    if (storedToken && storedUser) {
      let parsedUser: User | null = null;
      try {
        parsedUser = JSON.parse(storedUser);
      } catch {
        localStorage.removeItem("adminUser");
        localStorage.removeItem("adminToken");
      }
      if (parsedUser) {
        setToken(storedToken);
        setUser(parsedUser);
        setIsAuthenticated(true);
        if (import.meta.env.VITE_POSTHOG_KEY) {
          posthog.identify(parsedUser.id, {
            email: parsedUser.email,
            name: [parsedUser.firstName, parsedUser.lastName].filter(Boolean).join(' '),
            role: parsedUser.roleName,
          });
        }
      } else {
        setIsAuthenticated(false);
        setToken(null);
        setUser(null);
      }
    } else {
      setIsAuthenticated(false);
      setToken(null);
      setUser(null);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadAuthFromStorage();

    // Listen for custom auth change events (same tab)
    const handleAuthChange = () => {
      loadAuthFromStorage();
    };

    // Listen for storage changes (from other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "adminToken" || e.key === "adminUser") {
        loadAuthFromStorage();
        // If the user key was removed in another tab, reset PostHog identity
        if (e.key === "adminUser" && e.newValue === null && import.meta.env.VITE_POSTHOG_KEY) {
          posthog.reset();
        }
      }
    };

    window.addEventListener("authChange", handleAuthChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("authChange", handleAuthChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const logout = () => {
    if (import.meta.env.VITE_POSTHOG_KEY) {
      posthog.reset();
    }
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    // Drop cached server state so the next admin never sees the previous admin's data.
    queryClient.clear();
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    navigate("/login");
  };

  return {
    user,
    token,
    isAuthenticated,
    isLoading,
    logout,
  };
}
